import * as joint from '@joint/core'
import type {
  Relationship,
  RelationshipView,
  Configuration,
  ViewType,
} from '../types'
import {
  createRelationship,
  createSyncRelationship,
  createAsyncRelationship,
} from '../shapes'
import { resolveRelationshipStyle } from './styleResolver'

/**
 * Create a JointJS link for a relationship
 */
export function createLinkForRelationship(
  relationship: Relationship,
  relationshipView: RelationshipView,
  configuration?: Configuration,
  sequenceNumber?: string
): joint.shapes.standard.Link {
  const {
    id,
    sourceId,
    destinationId,
    description,
    technology,
    tags,
    interactionStyle,
  } = relationship

  const { vertices } = relationshipView

  // Resolve style based on tags and configuration
  const style = resolveRelationshipStyle(tags, interactionStyle, configuration)

  // Add sequence number if provided (for dynamic views)
  if (sequenceNumber) {
    style.sequenceNumber = sequenceNumber
  }

  // Create link based on interaction style
  if (interactionStyle === 'Synchronous') {
    return createSyncRelationship(
      id,
      sourceId,
      destinationId,
      description,
      technology,
      style,
      vertices
    )
  } else if (interactionStyle === 'Asynchronous') {
    return createAsyncRelationship(
      id,
      sourceId,
      destinationId,
      description,
      technology,
      style,
      vertices
    )
  }

  // Default to dashed (asynchronous-style) relationship
  return createRelationship(
    id,
    sourceId,
    destinationId,
    description,
    technology,
    style,
    vertices
  )
}

/**
 * Create links for all relationships in a view
 */
export function createLinksForView(
  relationshipViews: RelationshipView[],
  relationshipMap: Map<string, Relationship>,
  elementIds: Set<string>,
  configuration?: Configuration,
  viewType?: ViewType
): joint.shapes.standard.Link[] {
  const links: joint.shapes.standard.Link[] = []

  // For dynamic views, sort by order and assign sequence numbers
  let sortedViews = [...relationshipViews]
  if (viewType === 'Dynamic') {
    sortedViews = sortedViews.sort((a, b) => {
      const orderA = a.order ? parseFloat(a.order) : 0
      const orderB = b.order ? parseFloat(b.order) : 0
      return orderA - orderB
    })
  }

  let sequenceCounter = 1
  for (const relView of sortedViews) {
    const relationship = relationshipMap.get(relView.id)
    if (!relationship) continue

    // Only include relationships where both source and destination are in the view
    if (
      elementIds.has(relationship.sourceId) &&
      elementIds.has(relationship.destinationId)
    ) {
      // For dynamic views, use the order from the view or auto-increment
      const sequenceNumber = viewType === 'Dynamic'
        ? (relView.order || String(sequenceCounter))
        : undefined

      const link = createLinkForRelationship(relationship, relView, configuration, sequenceNumber)
      links.push(link)

      if (viewType === 'Dynamic') {
        sequenceCounter++
      }
    }
  }

  return links
}
